<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6 overflow-hidden">
    <!-- Consumption Selection/Header -->
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center shrink-0">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Consumption</h2>
      </div>

      <form class="px-6 pb-5 pt-2 shrink-0" @submit.prevent>
        <div class="flex flex-wrap items-center gap-x-12 gap-y-4">
          <div class="flex items-center gap-3 min-w-[240px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">
              Calendar:
            </label>
            <input
              v-model="currentDate"
              @change="fetchIngredients"
              type="date"
              @keydown.prevent
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
          </div>
          <div class="flex items-center gap-3 min-w-[280px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">
              Department:
            </label>
            <select
              v-model="selectedDepartment"
              @change="fetchIngredients"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select Department</option>
              <option v-for="dept in filteredDepartments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
            </select>
          </div>
          <div class="flex items-center gap-3 min-w-[340px] relative">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">
              Other Transfer:
            </label>
            <button
              type="button"
              @click.stop="toggleOtherTransferDropdown"
              :disabled="!selectedDepartment || !currentDate"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer text-left flex items-center justify-between gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span class="truncate">{{ incomingOtherTransfers.length }} Other Transfers</span>
              <span class="text-xs text-slate-500">▾</span>
            </button>
            <div
              v-if="showOtherTransferDropdown"
              class="absolute top-[calc(100%+6px)] right-0 max-w-[calc(100vw-2rem)] bg-white border border-[#cbd5e1] rounded-md shadow-xl z-40 overflow-x-auto"
              @click.stop
            >
              <div class="min-w-[920px]">
                <div class="px-3 py-2 text-[11px] font-bold text-[#0d4169] border-b border-slate-100 flex justify-between">
                  <span>Incoming Other Transfers</span>
                  <span class="text-[10px] text-slate-400">{{ incomingOtherTransfers.length }} record(s)</span>
                </div>
                <div class="max-h-[340px] overflow-y-auto custom-scrollbar">
                  <div
                    v-if="incomingOtherTransfers.length === 0"
                    class="px-3 py-3 text-xs text-slate-400"
                  >
                    No other transfers found.
                  </div>
                  <template v-else>
                    <div class="grid grid-cols-5 gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/70 text-[10px] font-bold text-[#0d4169]">
                      <span class="text-left">Department</span>
                      <span class="text-left">Item</span>
                      <span class="text-left">Quantity</span>
                      <span class="text-left">Select Item</span>
                      <span class="text-left">Actions</span>
                    </div>
                    <div
                      v-for="tr in incomingOtherTransfers"
                      :key="tr.id"
                      class="grid grid-cols-5 gap-2 px-3 py-2 border-b border-slate-50 last:border-0 hover:bg-slate-50 items-center"
                    >
                      <span class="text-xs font-medium text-slate-700 truncate text-left" :title="tr.senderDepartmentLabel">
                        {{ tr.senderDepartmentLabel || '-' }}
                      </span>
                      <span class="text-xs text-slate-600 truncate text-left" :title="tr.itemName">
                        {{ tr.itemName || '-' }}
                      </span>
                      <span class="text-xs font-bold text-emerald-700 text-left">
                        {{ Number(tr.quantity || 0).toFixed(2) }}
                      </span>
                      <select
                        v-model="selectedReceiverItemByTransfer[tr.id]"
                        class="w-full px-2 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] text-xs bg-white text-left"
                      >
                        <option value="">Select Item</option>
                        <option v-for="opt in listedIngredientOptions" :key="opt.valueKey" :value="String(opt.id)">
                          {{ opt.label }}
                        </option>
                      </select>
                      <div class="flex items-center gap-1 justify-start">
                        <button
                          type="button"
                          @click.stop="handleIncomingOtherTransferDecision(tr, 1)"
                          :disabled="processingIncomingTransferIds[tr.id]"
                          class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          @click.stop="handleIncomingOtherTransferDecision(tr, 2)"
                          :disabled="processingIncomingTransferIds[tr.id]"
                          class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <template v-else>
        <table class="w-full consumption-table min-w-max">
          <thead>
            <tr>
              <th class="w-10 text-center sticky-col bg-[#f1f5f9]">No.</th>
              <th class="min-w-[120px] sticky-col left-[40px] bg-[#f1f5f9]">Ingredient Name</th>
              <th class="w-20 text-center">Bbf/Bignin/</th>
              <th class="w-20 text-center">Received Store</th>
              <th class="w-20 text-center">Received Non.Stockable</th>
              <th class="w-20 text-center">Product</th>
              <th class="w-24 text-center ">Transfer Recived (D)</th>
              <th class="w-24 text-center ">Transfer Recived (T)</th>
              <th class="w-20 text-center">Wastage</th>
              <th class="w-24 text-center font-bold bg-slate-100">Total On Hand</th>
              <th class="w-24 text-center">Sales Manwale</th>
              <th class="w-24 text-center">Sales System</th>
              <th class="w-20 text-center">Managment</th>
              <th class="w-20 text-center">Room</th>
              <th class="w-20 text-center">Promotion</th>
              <th class="w-24 text-center">Company Cost</th>
              <th class="w-20 text-center">Spoilage</th>
              <th class="w-20 text-center">Other</th>
              <th class="w-24 text-center ">Transfer Issu (D)</th>
              <th class="w-24 text-center ">Transfer Issu (T)</th>
              <th class="w-24 text-center">Spoiled By Items</th>
              <th class="w-24 text-center">Other/Catering</th>
              <th class="w-24 text-center font-bold bg-blue-50">Total Active</th>
              <th class="w-20 text-center ">Balance</th>
              <th class="w-20 text-center">Ending</th>
              <th class="w-20 text-center font-bold bg-red-50 text-red-700">Deference</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in consumptions" :key="index" :class="['hover:bg-slate-50 transition-colors group', row.type === 'product' ? 'bg-white' : '']">
              <td class="text-center sticky-col group-hover:bg-slate-50" :class="row.type === 'product' ? 'bg-white' : 'bg-white'">{{ index + 1 }}</td>
              <td class="sticky-col left-[40px] group-hover:bg-slate-50 font-medium" :class="row.type === 'product' ? 'bg-white text-emerald-800' : 'bg-white'">
                {{ row.ingredient }}
              </td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.bbf }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.storeRecv }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.nonStock }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.production }}</td>
              <!-- Transfer Recived 1: Details Popover -->
              <td>
                <div v-if="row.transInDetails && row.transInDetails.length > 0" class="flex items-center justify-center">
                  <button 
                    @click.stop="openDropdown($event, index, 'in')"
                    :disabled="isFutureDate"
                    class="w-full px-2.5 py-0.5 border border-[#cbd5e1] rounded-md outline-none text-xs bg-white cursor-pointer text-left flex items-center justify-between gap-1 hover:border-[#0d4169] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span class="text-emerald-700 font-semibold">Details ▾</span>
                  </button>
                </div>
                <div v-else class="text-center text-slate-300">-</div>
              </td>
              <td class="bg-emerald-50/30 text-emerald-700 text-sm font-bold text-center">{{ row.transIn1 }}</td>
              <td><input :disabled="consumptionFieldDisabled(row, 'wastage')" v-model.number="row.wastage" type="number" @input="calculateFields(index)" class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none bg-red-100 text-slate-500 focus:bg-white transition-all text-sm text-center font-semibold  disabled:opacity-60 disabled:cursor-not-allowed"></td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ row.totalAvail }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.salesManual }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.salesSystem }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.mgmt }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.room }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.promo }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.coCost }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.spoilage }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.other }}</td>
              <!-- Transfer Issu 1: Dropdown/Input Popover -->
              <td>
                <div class="flex items-center justify-center">
                  <button 
                    @click.stop="openDropdown($event, index, 'out')"
                    :disabled="isFutureDate"
                    class="w-full px-2.5 py-0.5 border border-[#cbd5e1] rounded-md outline-none text-xs bg-white cursor-pointer text-left flex items-center justify-between gap-1 hover:border-[#0d4169] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span
                      class="font-black text-center bg-slate-50/30"
                      :class="hasPendingTransferIssue(row) ? 'text-red-600' : 'text-[#0d4169]'"
                    >{{ hasTransferIssueQuantity(row) ? 'Edit ▾' : 'Send To ▾' }}</span>
                  </button>
                </div>
              </td>
              <!-- Transfer Issu 2: Total Sum -->
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ row.transOut1 }}</td>
              <td><input :disabled="consumptionFieldDisabled(row, 'spoiledItem')" v-model.number="row.spoiledItem" type="number" @input="calculateFields(index)" class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none bg-red-100 text-slate-500 focus:bg-white transition-all text-sm text-center font-semibold disabled:opacity-60 disabled:cursor-not-allowed"></td>
              <td><input :disabled="consumptionFieldDisabled(row, 'catering')" v-model.number="row.catering" type="number" @input="calculateFields(index)" class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none bg-red-100 text-slate-500 focus:bg-white transition-all text-sm text-center font-semibold  disabled:opacity-60 disabled:cursor-not-allowed"></td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ row.totalUsed }}</td>
              <td class="font-black text-green-700 text-center text-slate-500">{{ row.balance }}</td>
              <td class="bg-slate-50/50 text-slate-500 text-sm text-center">{{ row.ending }}</td>
              <td class="font-black text-center bg-red-50/30" :class="{'text-red-500': row.variance < 0, 'text-green-500': row.variance > 0}">{{ row.variance }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="consumptions.length === 0" class="flex flex-col items-center py-20 text-slate-400">
           <svg class="w-12 h-12 mb-2 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>
           <p class="text-sm font-bold">No ingredients found for this department.</p>
        </div>

        <div v-if="consumptions.length > 0" class="flex justify-center items-center gap-4 mt-6">
          <button
            type="button"
            @click="openSaveConsumptionModal"
            :disabled="!canClickSaveChanges"
            class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <span v-if="saving" class="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"></span>
            <span>{{ saving ? 'Saving...' : (isSavedData ? 'Update Changes' : 'Save Changes') }}</span>
          </button>

          <div v-if="saveStatus === 'success'" class="flex items-center gap-1.5 text-emerald-600 font-bold text-sm bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-md">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path></svg>
            Saved Successfully
          </div>
          <div v-if="saveStatus === 'error'" class="flex items-center gap-1.5 text-red-600 font-bold text-sm bg-red-50 border border-red-200 px-3 py-1.5 rounded-md">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            Failed to save
          </div>
        </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Global fixed dropdown panel - Teleported to body to escape overflow clipping -->
  <Teleport to="body">
    <div
      v-if="activePopover.index >= 0"
      :style="popoverStyle"
      class="fixed z-[9999] bg-white border border-[#cbd5e1] rounded-md shadow-xl"
      @click.stop
    >
      <!-- Transfer Received details -->
      <template v-if="activePopover.type === 'in'">
        <div class="px-3 py-2 text-[11px] font-bold text-[#0d4169] border-b border-slate-100 flex justify-between">
          <span>Incoming Sources</span>
          <span class="text-[10px] text-slate-400">{{ (consumptions[activePopover.index]?.transInDetails || []).length }} dept(s)</span>
        </div>
        <div class="overflow-y-auto custom-scrollbar" :style="{ maxHeight: popoverBodyMaxHeight }">
          <div
            v-for="(det, i) in consumptions[activePopover.index]?.transInDetails || []"
            :key="det.name + i"
            class="flex items-center gap-2 px-3 py-1.5 border-b border-slate-50 last:border-0 hover:bg-slate-50"
          >
            <span class="text-xs font-medium text-slate-700 truncate flex-1">{{ det.name }}</span>
            <span class="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">{{ det.quantity }}</span>
            
            <div class="flex items-center gap-1 shrink-0">
              <template v-if="!det.isOtherTransfer && det.is_confirmed === 0">
                <button @click.stop="handleConfirmTransfer(activePopover.index, i, 1)" class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors">Accept</button>
                <button @click.stop="handleConfirmTransfer(activePopover.index, i, 2)" class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 hover:bg-red-200 transition-colors">Reject</button>
              </template>
              <span v-else-if="det.isOtherTransfer || det.is_confirmed === 1" class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Confirmed</span>
              <span v-else-if="det.is_confirmed === 2" class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Rejected</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Transfer Issue inputs -->
      <template v-if="activePopover.type === 'out'">
        <div class="px-3 py-2 text-[11px] font-bold text-[#0d4169] border-b border-slate-100 flex items-center justify-between gap-2">
          <span class="truncate flex-1 min-w-0" :title="consumptions[activePopover.index]?.ingredient">
            {{ consumptions[activePopover.index]?.ingredient || 'Ingredient' }}
          </span>
          <div class="flex items-center gap-2">
            <button
              v-if="!isFinalized && !isFutureDate"
              type="button"
              @click.stop="savePartialTransferIssue(activePopover.index)"
              :disabled="!canPartialSaveTransferIssue(consumptions[activePopover.index]) || partialTransferSaving"
              class="px-2 py-0.5 bg-[#0d4169] text-white text-[10px] font-bold rounded hover:bg-[#0a3354] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="partialTransferSaving" class="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              <span v-else>Save</span>
            </button>
            <span class="text-[10px] font-bold text-[#0d4169]">{{ transferIssuePopoverTotal(consumptions[activePopover.index]).toFixed(2) }} Total</span>
          </div>
        </div>
        <div class="px-3 py-1.5 border-b border-slate-100 bg-slate-50/70 grid grid-cols-[1fr_56px_72px] gap-2 text-[10px] font-bold text-[#0d4169]">
          <span class="text-left">Department</span>
          <span class="text-center">T. Sent</span>
          <span class="text-center">Qty</span>
        </div>
        <div class="pb-10 overflow-y-auto custom-scrollbar" :style="{ maxHeight: popoverBodyMaxHeight }">
          <div
            v-for="det in transferOutDetailsForPopover(consumptions[activePopover.index])"
            :key="det.id"
            class="grid grid-cols-[1fr_56px_72px] gap-2 items-center px-3 py-1.5 border-b border-slate-50 last:border-0 hover:bg-slate-50"
          >
            <label class="text-xs font-medium text-slate-700 truncate text-left" :title="det.displayLabel || det.name">{{ det.displayLabel || det.name }}</label>
            <span class="flex items-center justify-center gap-0.5 min-w-0">
              <span class="text-xs font-bold text-slate-700">{{ formatTransferQty(det.tSent) }}</span>
              <template v-if="getTransferTSent(det) > 0">
                <svg
                  v-if="getTransferIssueApprovalStatus(det) === 'approved'"
                  class="w-3 h-3 shrink-0 text-emerald-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  title="Accepted"
                >
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <svg
                  v-else-if="getTransferIssueApprovalStatus(det) === 'rejected'"
                  class="w-3 h-3 shrink-0 text-red-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  title="Rejected"
                >
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
                <svg
                  v-else
                  class="w-3 h-3 shrink-0 text-amber-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  title="Pending"
                >
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
                </svg>
              </template>
            </span>
            <input
              v-model="det.quantity"
              type="number"
              min="0"
              step="any"
              inputmode="decimal"
              :disabled="transferIssueInputDisabled(consumptions[activePopover.index])"
              @input="onTransferIssueQtyInput(det, activePopover.index)"
              @blur="sanitizeTransferIssueQty(det, activePopover.index)"
              class="w-full px-1.5 py-1 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs font-bold text-center text-[#0d4169] bg-white transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder="0.00"
            >
          </div>
          <div
            v-if="isFinalized && consumptions[activePopover.index]?.canEditTransferIssueD"
            class="px-3 py-2 border-t border-slate-100"
          >
            <div
              v-for="det in transferOutDetailsForPopover(consumptions[activePopover.index]).filter(d => (Number(d.tSent) || 0) > 0)"
              :key="'status-' + det.id"
              class="flex items-center justify-end gap-1 py-0.5"
            >
              <span
                class="text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                :class="det.is_confirmed === 1 ? 'bg-emerald-100 text-emerald-700' : (det.is_confirmed === 2 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700')"
              >
                {{ det.displayLabel || det.name }}:
                {{ det.is_confirmed === 1 ? 'Confirmed' : (det.is_confirmed === 2 ? 'Rejected' : 'Not Confirmed') }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </Teleport>

  <!-- Save confirmation modal (same flow as balanceReport.vue Confirm Save Report) -->
  <div v-if="saveConsumptionModal.open" class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeSaveConsumptionModal">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <div class="text-left">
          <h3 class="text-base font-bold m-0">Confirm Save Consumption</h3>
        </div>
        <button type="button" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeSaveConsumptionModal" aria-label="Close save modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="p-5 space-y-4">
        <div class="text-xs font-semibold text-slate-500">Please enter the code below to confirm that you want to save.</div>

        <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
          {{ saveConsumptionModal.code }}
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
          <input
            v-model.trim="saveConsumptionModal.input"
            type="text"
            maxlength="4"
            class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
          >
        </div>
        <div v-if="saveConsumptionModal.error" class="text-xs text-red-600 font-semibold">{{ saveConsumptionModal.error }}</div>
      </div>

      <div class="px-5 py-4 flex items-center justify-end gap-2">
        <button type="button" @click="closeSaveConsumptionModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
        <button
          type="button"
          @click="confirmSaveConsumption"
          :disabled="saving"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
        >
          {{ saving ? 'Saving...' : 'Confirm' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { apiFetch } from '../../../utils/api'
import store from '../../../store'

const selectedDepartment = ref('')
const currentDate = ref('')
const departments = ref([])
/** Company-wide active departments for Transfer Issu (D) — includes all branches. */
const transferIssueDepartments = ref([])
const consumptions = ref([])
const loading = ref(false)
const activePopover = ref({ index: -1, type: '' })
const popoverStyle = ref({})
const popoverBodyMaxHeight = ref('240px')
const isSavedData = ref(false)
const isFinalized = ref(false)
const partialTransferSaving = ref(false)
const showOtherTransferDropdown = ref(false)
const incomingOtherTransfers = ref([])
const selectedReceiverItemByTransfer = ref({})
const processingIncomingTransferIds = ref({})

/** Local calendar YYYY-MM-DD (matches <input type="date>), avoids UTC vs local mismatches */
function formatLocalYmd(d = new Date()) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const todayLocalYmd = computed(() => formatLocalYmd(new Date()))

const isFutureDate = computed(() => {
  if (!currentDate.value) return false
  return currentDate.value > todayLocalYmd.value
})

function hasTransferIssueQuantity(row) {
  return (row?.transOutDetails || []).some((det) => getTransferTSent(det) > 0)
}

function getTransferTSent(det) {
  return Number(det?.tSent ?? det?._savedQty ?? 0) || 0
}

function getTransferIssueApprovalStatus(det) {
  if (!det) return 'pending'
  const status = Number(det?.is_approved ?? det?.is_confirmed ?? 0)
  if (status === 1) return 'approved'
  if (status === 2) return 'rejected'
  return 'pending'
}

function parseTransferQty(value) {
  if (value === '' || value === null || value === undefined) return null
  const qty = parseFloat(String(value).trim())
  return Number.isFinite(qty) ? qty : null
}

function formatTransferQty(value) {
  const num = parseTransferQty(value)
  if (num === null || num === 0) return ''
  return num
}

function normalizeTransOutDetail(det, { editableReplace = false } = {}) {
  const storedQty = Number(det?.quantity ?? 0) || 0
  const tSent = Number(det?.tSent ?? det?._savedQty ?? storedQty) || 0
  return {
    id: det.id,
    name: det.name,
    displayLabel: det.displayLabel,
    tSent,
    quantity: editableReplace ? (tSent > 0 ? tSent : '') : '',
    is_confirmed: Number(det?.is_confirmed ?? 0) || 0,
    is_approved: Number(det?.is_approved ?? det?.is_confirmed ?? 0) || 0,
  }
}

function transferIssuePopoverTotal(row) {
  return (row?.transOutDetails || []).reduce((sum, det) => {
    const pending = parseTransferQty(det?.quantity) || 0
    return sum + getTransferTSent(det) + pending
  }, 0)
}

function transferIssueInputDisabled(row) {
  if (isFutureDate.value) return true
  if (isFinalized.value) return !row?.canEditTransferIssueD
  return false
}

function onTransferIssueQtyInput(det, rowIndex) {
  const raw = String(det?.quantity ?? '')
  if (raw.includes('-')) {
    det.quantity = raw.replace(/-/g, '')
  }
  const row = consumptions.value[rowIndex]
  if (isFinalized.value && row?.canEditTransferIssueD) {
    updateTransSum(rowIndex, 'out')
    calculateFields(rowIndex, true)
  }
}

function sanitizeTransferIssueQty(det, rowIndex) {
  const qty = parseTransferQty(det?.quantity)
  if (qty === null || qty <= 0) {
    det.quantity = ''
  } else {
    det.quantity = qty
  }
  const row = consumptions.value[rowIndex]
  if (isFinalized.value && row?.canEditTransferIssueD) {
    updateTransSum(rowIndex, 'out')
    calculateFields(rowIndex, true)
  }
}

function getTransferDetailsWithFilledQty(row) {
  return (row?.transOutDetails || []).filter((det) => {
    const qty = parseTransferQty(det?.quantity)
    return qty !== null && qty > 0
  })
}

function canPartialSaveTransferIssue(row) {
  if (partialTransferSaving.value || isFutureDate.value || isFinalized.value) return false
  if (!selectedDepartment.value || !currentDate.value) return false
  return getTransferDetailsWithFilledQty(row).length > 0
}

/** Production in Total On Hand: subtract for items, add for products (and other types). */
function productionContributionForTotalAvail(row, production) {
  const prod = Number(production) || 0
  return String(row?.type || '').toLowerCase() === 'item' ? -prod : prod
}

function computeTotalAvail(row, { bbf, storeRecv, nonStock, production, wastage, transIn1, transIn2 }) {
  const productionTerm = productionContributionForTotalAvail(row, production)
  return Number((
    (Number(bbf) || 0) +
    (Number(storeRecv) || 0) +
    (Number(nonStock) || 0) +
    productionTerm -
    (Number(wastage) || 0) +
    (Number(transIn1) || 0) +
    (Number(transIn2) || 0)
  ).toFixed(2))
}

/** Red "Edit" when any issued transfer qty > 0 is not confirmed (pending / rejected). */
function hasPendingTransferIssue(row) {
  return (row?.transOutDetails || []).some((det) => {
    const qty = getTransferTSent(det)
    if (qty <= 0) return false
    return Number(det?.is_confirmed ?? 0) !== 1
  })
}

/** Finalized rows: only `consumption_edit_access` flags matter. Live / partial draft: editable for today and past dates. */
function consumptionFieldDisabled(row, field) {
  if (isFutureDate.value) return true
  const key =
    field === 'wastage'
      ? 'canEditWastage'
      : field === 'transferIssueD'
        ? 'canEditTransferIssueD'
        : field === 'spoiledItem'
          ? 'canEditSpoiledItem'
          : field === 'catering'
            ? 'canEditCatering'
            : null
  if (!key) return true
  if (isFinalized.value) return !row[key]
  return false
}

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

// All departments from token as names list
const userDeptList = computed(() => {
  const depts = currentUser.value?.departments || currentUser.value?.department?.name || currentUser.value?.departmentName || currentUser.value?.dept
  if (!depts) return []
  if (typeof depts === 'string') return depts.split(',').map(d => d.trim()).filter(Boolean)
  if (Array.isArray(depts)) return depts.filter(Boolean)
  return [depts].filter(Boolean)
})

const filteredDepartments = computed(() => {
  if (!userDeptList.value.length) return departments.value
  return departments.value.filter(d =>
    userDeptList.value.some(userDept => userDept.toLowerCase() === String(d.name || '').toLowerCase())
  )
})

const listedIngredientOptions = computed(() => {
  const unique = new Map()
  ;(consumptions.value || []).forEach((row) => {
    const type = String(row?.type || 'item').toLowerCase() === 'product' ? 'product' : 'item'
    const id = Number(row?.ingredientId ?? row?.itemId ?? row?.productId)
    if (!Number.isFinite(id)) return
    const key = `${type}_${id}`
    if (unique.has(key)) return
    unique.set(key, {
      id,
      type,
      valueKey: key,
      label: `${row?.ingredient || 'Unknown'} (${type})`,
    })
  })
  return Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label))
})

watch(filteredDepartments, (newVal) => {
  if (newVal.length === 1 && !selectedDepartment.value) {
    selectedDepartment.value = newVal[0].id
    setTimeout(() => { fetchIngredients() }, 50)
  }
}, { deep: true })

function applyPendingEditPermissions(rows, pendingList) {
  const list = Array.isArray(pendingList) ? pendingList : []
  return rows.map((row) => {
    const rid = row.id ?? row.consumptionRecordId
    const relevant = list.filter((p) => Number(p.consumptionRecordId) === Number(rid))
    return {
      ...row,
      canEditWastage: relevant.some((p) => p.wastage === true),
      canEditTransferIssueD: relevant.some((p) => p.transferIssueD === true),
      canEditSpoiledItem: relevant.some((p) => p.spoiledItem === true),
      canEditCatering: relevant.some((p) => p.catering === true),
    }
  })
}

const openDropdown = (event, index, type) => {
  if (isFutureDate.value) return

  if (activePopover.value.index === index && activePopover.value.type === type) {
    activePopover.value = { index: -1, type: '' }
    return
  }
  const rect = event.currentTarget.getBoundingClientRect()
  const panelWidth = type === 'out' ? 360 : 380
  
  // Calculate horizontal position
  let left = rect.left
  if (left + panelWidth > window.innerWidth - 8) {
    left = window.innerWidth - panelWidth - 8
  }
  
  // Calculate vertical placement + dynamic body max-height to avoid clipping at viewport bottom.
  const edgeGap = 8
  const headerHeight = 44
  const spaceBelow = Math.max(120, Math.floor(window.innerHeight - rect.bottom - edgeGap))
  const spaceAbove = Math.max(120, Math.floor(rect.top - edgeGap))
  const openUp = spaceBelow < 260 && spaceAbove > spaceBelow

  let verticalStyle = {}
  let bodyMax = 240
  if (openUp) {
    verticalStyle = { bottom: (window.innerHeight - rect.top + 2) + 'px' }
    bodyMax = Math.max(120, spaceAbove - headerHeight)
  } else {
    verticalStyle = { top: (rect.bottom + 2) + 'px' }
    bodyMax = Math.max(120, spaceBelow - headerHeight)
  }
  popoverBodyMaxHeight.value = `${bodyMax}px`

  popoverStyle.value = {
    ...verticalStyle,
    left: left + 'px',
    minWidth: panelWidth + 'px',
  }
  activePopover.value = { index, type }
}

const closePopovers = () => {
  activePopover.value = { index: -1, type: '' }
  showOtherTransferDropdown.value = false
}

function toggleOtherTransferDropdown() {
  showOtherTransferDropdown.value = !showOtherTransferDropdown.value
}

function transferOutDetailsForPopover(row) {
  if (!row) return []
  return (row.transOutDetails || []).filter(
    (d) =>
      String(d.id) !== String(selectedDepartment.value) &&
      transferIssueDepartments.value.some((dep) => String(dep.id) === String(d.id))
  )
}

function syncTransferOutDetailsWithCompanyDepartments() {
  if (!transferIssueDepartments.value.length) return
  const allowedIds = new Set(transferIssueDepartments.value.map((d) => String(d.id)))
  const selfId = String(selectedDepartment.value || '')

  consumptions.value.forEach((row) => {
    const existing = Array.isArray(row.transOutDetails) ? row.transOutDetails : []
    const byId = new Map(existing.map((d) => [String(d.id), normalizeTransOutDetail(d, { editableReplace: isFinalized.value && row.canEditTransferIssueD })]))

    for (const dept of transferIssueDepartments.value) {
      const id = String(dept.id)
      if (id === selfId) continue
      if (!byId.has(id)) {
        byId.set(id, {
          id: dept.id,
          name: dept.name,
          displayLabel: dept.displayLabel,
          tSent: 0,
          quantity: '',
          is_confirmed: 0,
          is_approved: 0,
        })
      } else {
        const entry = byId.get(id)
        entry.displayLabel = dept.displayLabel
        entry.name = dept.name
      }
    }

    row.transOutDetails = Array.from(byId.values())
      .filter((d) => String(d.id) !== selfId && allowedIds.has(String(d.id)))
      .map((d) => normalizeTransOutDetail(d, { editableReplace: isFinalized.value && row.canEditTransferIssueD }))
  })
}

function mergeSavedTransOutDetails(liveDetails, savedDetails, editableReplace = false) {
  const byId = new Map(
    (Array.isArray(liveDetails) ? liveDetails : []).map((d) => [
      String(d.id),
      normalizeTransOutDetail(d, { editableReplace }),
    ])
  )

  for (const savedDet of Array.isArray(savedDetails) ? savedDetails : []) {
    const id = String(savedDet?.id ?? '')
    if (!id) continue
    const savedQty = Number(savedDet?.quantity ?? 0) || 0
    const existing = byId.get(id)
    if (existing) {
      existing.tSent = savedQty
      existing.is_confirmed = Number(savedDet?.is_confirmed ?? existing.is_confirmed ?? 0) || 0
      existing.is_approved = Number(savedDet?.is_approved ?? savedDet?.is_confirmed ?? existing.is_approved ?? 0) || 0
      existing.quantity = editableReplace && savedQty > 0 ? savedQty : ''
    } else {
      byId.set(id, normalizeTransOutDetail({ ...savedDet, tSent: savedQty, quantity: savedQty }, { editableReplace }))
    }
  }

  return Array.from(byId.values())
}

function applySavedTransOutDetailsToRows(rows, savedRows, editableReplace = false) {
  const savedMap = new Map(
    (Array.isArray(savedRows) ? savedRows : []).map((row) => {
      const type = String(row?.type || '').toLowerCase()
      const id = row?.ingredientId ?? row?.itemId ?? row?.productId
      return [`${type}_${id}`, row]
    })
  )

  return rows.map((row) => {
    const type = String(row?.type || '').toLowerCase()
    const id = row?.ingredientId ?? row?.itemId ?? row?.productId
    const savedRow = savedMap.get(`${type}_${id}`)
    if (!savedRow) return row

    const transOutDetails = mergeSavedTransOutDetails(
      row.transOutDetails,
      savedRow.transOutDetails,
      editableReplace && row.canEditTransferIssueD
    )

    return {
      ...row,
      id: savedRow.id ?? savedRow.consumptionRecordId ?? row.id,
      consumptionRecordId: savedRow.consumptionRecordId ?? savedRow.id ?? row.consumptionRecordId,
      transOutDetails,
      transOut1: Number(transOutDetails.reduce((sum, d) => sum + (d.is_confirmed === 1 ? getTransferTSent(d) : 0), 0).toFixed(2)),
    }
  })
}

onMounted(() => {
  // currentDate.value = formatLocalYmd(new Date())
  fetchDepartments()
  fetchTransferIssueDepartments()
  window.addEventListener('click', closePopovers)
})

onUnmounted(() => {
  window.removeEventListener('click', closePopovers)
})

async function fetchDepartments() {
  try {
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : (companyId.value ? `companyId=${companyId.value}` : '')

    const url = query ? `/departments?${query}` : '/departments'

    const res = await apiFetch(url)
    if (!res.ok) return
    const data = await res.json().catch(() => [])

    const raw = Array.isArray(data) ? data : []
    // Keep only active departments; userDeptList filter happens in filteredDepartments
    departments.value = raw.filter(d => {
      if (Number(d.status ?? 1) !== 1) return false
      const isCentral = d.isCentral ?? d.is_central
      return isCentral === 0 || isCentral === false
    })
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error fetching departments:', err)
  }
}

async function fetchTransferIssueDepartments() {
  if (!companyId.value) {
    transferIssueDepartments.value = []
    return
  }
  try {
    const res = await apiFetch(`/departments/company-transfer-departments/${companyId.value}`)
    if (!res.ok) return
    const data = await res.json().catch(() => ({}))
    const list = Array.isArray(data?.departments) ? data.departments : []
    transferIssueDepartments.value = list.sort((a, b) =>
      String(a.displayLabel || a.name).localeCompare(String(b.displayLabel || b.name))
    )
    syncTransferOutDetailsWithCompanyDepartments()
  } catch (err) {
    console.error('Error fetching transfer issue departments:', err)
    transferIssueDepartments.value = []
  }
}

const updateTransSum = (index, direction) => {
  if (isFutureDate.value) return
  const row = consumptions.value[index]
  if (!row) return

  if (direction === 'out') {
    const total = (row.transOutDetails || []).reduce((sum, det) => {
      if (isFinalized.value && row.canEditTransferIssueD) {
        const qty = parseTransferQty(det.quantity) ?? getTransferTSent(det)
        return sum + (det.is_confirmed === 1 ? qty : 0)
      }
      return sum + (det.is_confirmed === 1 ? getTransferTSent(det) : 0)
    }, 0)
    row.transOut1 = Number(total.toFixed(2))
  } else if (direction === 'in') {
    const total = (row.transInDetails || []).reduce((sum, det) => sum + ((det.is_confirmed === 1) ? (Number(det.quantity) || 0) : 0), 0)
    row.transIn1 = Number(total.toFixed(2))
  }
  
  calculateFields(index)
}

const handleConfirmTransfer = async (rowIndex, detIndex, status) => {
  const row = consumptions.value[rowIndex]
  if (!row) return
  const det = row.transInDetails[detIndex]
  if (!det || !det.consumptionRecordId) return

  try {
    const res = await apiFetch('/consumption/confirm-transfer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        consumptionRecordId: det.consumptionRecordId,
        receiverDepId: selectedDepartment.value,
        is_confirmed: status
      })
    })

    if (res.ok) {
      det.is_confirmed = status
      updateTransSum(rowIndex, 'in')
    } else {
      console.error('Failed to update transfer status')
    }
  } catch (err) {
    console.error('Error confirming transfer:', err)
  }
}

const saving = ref(false)
const saveStatus = ref('') // 'success' | 'error' | ''

/** Live draft: any date except future. Saved: pending edit-access still enforced per field. */
const canClickSaveChanges = computed(() => {
  if (saving.value) return false
  if (isFutureDate.value) return false
  if (consumptions.value.length === 0) return false
  return true
})

const saveConsumptionModal = ref({
  open: false,
  code: '',
  input: '',
  error: ''
})

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveConsumptionModal() {
  if (!canClickSaveChanges.value) return
  saveConsumptionModal.value.open = true
  saveConsumptionModal.value.code = generateVerificationCode()
  saveConsumptionModal.value.input = ''
  saveConsumptionModal.value.error = ''
}

function closeSaveConsumptionModal() {
  if (saving.value) return
  saveConsumptionModal.value.open = false
}

async function confirmSaveConsumption() {
  const expected = String(saveConsumptionModal.value.code || '').trim()
  const entered = String(saveConsumptionModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveConsumptionModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveConsumptionModal.value.error = ''
  const ok = await saveAllRows()
  if (ok) {
    saveConsumptionModal.value.open = false
  }
}

async function savePartialTransferIssue(rowIndex) {
  const row = consumptions.value[rowIndex]
  if (!row || !canPartialSaveTransferIssue(row)) return

  const detailsToSave = getTransferDetailsWithFilledQty(row)
  partialTransferSaving.value = true
  try {
    const res = await apiFetch('/consumption/partial-transfer-issue', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        departmentId: selectedDepartment.value,
        calendar: currentDate.value,
        type: row.type,
        itemId: row.type === 'item' ? (row.ingredientId || row.itemId) : null,
        productId: row.type === 'product' ? (row.ingredientId || row.productId) : null,
        ingredient: row.ingredient,
        transOutDetails: detailsToSave.map((det) => ({
          id: det.id,
          name: det.name,
          quantity: parseTransferQty(det.quantity) || 0,
        })),
      }),
    })

    if (!res.ok) {
      const errPayload = await res.json().catch(() => ({}))
      window.alert(errPayload?.message || 'Failed to save transfer issue quantities.')
      return
    }

    const payload = await res.json().catch(() => ({}))
    const savedDetails = Array.isArray(payload?.transOutDetails) ? payload.transOutDetails : []
    const savedById = new Map(savedDetails.map((det) => [String(det.id), det]))

    row.transOutDetails = (row.transOutDetails || []).map((det) => {
      const savedDet = savedById.get(String(det.id))
      if (!savedDet) {
        return { ...det, quantity: '' }
      }
      return normalizeTransOutDetail({
        ...det,
        ...savedDet,
        tSent: Number(savedDet.quantity || 0),
        quantity: '',
      })
    })
    row.transOut1 = Number(payload?.transOut1 ?? row.transOut1 ?? 0)
    if (payload?.consumptionRecordId) {
      row.id = payload.consumptionRecordId
      row.consumptionRecordId = payload.consumptionRecordId
    }
    updateTransSum(rowIndex, 'out')
    syncTransferOutDetailsWithCompanyDepartments()
  } catch (err) {
    console.error('Error saving partial transfer issue:', err)
    window.alert('Failed to save transfer issue quantities.')
  } finally {
    partialTransferSaving.value = false
  }
}

async function fetchIngredients() {
  if (!selectedDepartment.value || !currentDate.value) {
    incomingOtherTransfers.value = []
    selectedReceiverItemByTransfer.value = {}
    showOtherTransferDropdown.value = false
    return
  }
  await fetchIncomingOtherTransfers()
  loading.value = true
  try {
    const getRowKey = (row) => {
      const type = String(row?.type || '').toLowerCase()
      const id = row?.ingredientId ?? row?.itemId ?? row?.productId
      return `${type}_${id}`
    }

    // "Bbf/Bignin" column is row.bbf only in this UI; value is totalQty/total_qty from daily_inventory
    // (latest row per item/product with calendar <= selected date), filled by GET /consumption/ingredients.

    if (isFutureDate.value) {
      const previewDate = todayLocalYmd.value
      const liveRes = await apiFetch(`/consumption/ingredients?departmentId=${selectedDepartment.value}&date=${previewDate}`)
      if (liveRes.ok) {
        const data = await liveRes.json()
        consumptions.value = (Array.isArray(data) ? data : [])
          .sort((a, b) => (a.ingredient || '').localeCompare(b.ingredient || ''))
        isSavedData.value = false
        isFinalized.value = false
        syncTransferOutDetailsWithCompanyDepartments()
      } else {
        consumptions.value = []
        isSavedData.value = false
        isFinalized.value = false
      }
      return
    }

    const savedRes = await apiFetch(`/consumption/saved?departmentId=${selectedDepartment.value}&calendar=${currentDate.value}`)

    if (savedRes.ok) {
      const savedPayload = await savedRes.json().catch(() => ({ hasData: false, rows: [], isFinalized: false }))
      const finalized = Boolean(savedPayload?.isFinalized)
      isFinalized.value = finalized
      isSavedData.value = Boolean(savedPayload?.hasData && finalized)

      if (savedPayload?.hasData && Array.isArray(savedPayload.rows) && savedPayload.rows.length > 0) {
        let liveRows = []
        const liveResForSaved = await apiFetch(`/consumption/ingredients?departmentId=${selectedDepartment.value}&date=${currentDate.value}`)
        if (liveResForSaved.ok) {
          const liveData = await liveResForSaved.json().catch(() => [])
          liveRows = Array.isArray(liveData) ? liveData : []
        }
        const liveMap = new Map(liveRows.map((r) => [getRowKey(r), r]))

        if (finalized) {
          const mergedRows = savedPayload.rows.map((savedRow) => {
            const liveRow = liveMap.get(getRowKey(savedRow))
            if (!liveRow) {
              return {
                ...savedRow,
                production: 0,
              }
            }
            return {
              ...liveRow,
              id: savedRow.id ?? savedRow.consumptionRecordId ?? liveRow.id,
              consumptionRecordId: savedRow.consumptionRecordId ?? savedRow.id ?? liveRow.consumptionRecordId,
              type: savedRow.type ?? liveRow.type,
              ingredientId: savedRow.ingredientId ?? liveRow.ingredientId,
              itemId: savedRow.itemId ?? liveRow.itemId,
              productId: savedRow.productId ?? liveRow.productId,
              ingredient: savedRow.ingredient ?? liveRow.ingredient,
              wastage: savedRow.wastage,
              totalAvail: savedRow.totalAvail,
              production: Number(liveRow.production || 0),
              transOutDetails: Array.isArray(savedRow.transOutDetails)
                ? savedRow.transOutDetails.map((d) => normalizeTransOutDetail({ ...d, tSent: d.quantity }, { editableReplace: false }))
                : [],
              transOut1: Array.isArray(savedRow.transOutDetails)
                ? Number(savedRow.transOutDetails.reduce((sum, d) => sum + (d.is_confirmed === 1 ? (Number(d.quantity) || 0) : 0), 0).toFixed(2))
                : 0,
              spoiledItem: savedRow.spoiledItem,
              catering: savedRow.catering,
              totalUsed: savedRow.totalUsed,
              balance: savedRow.balance,
              variance: savedRow.variance,
            }
          })

          consumptions.value = applyPendingEditPermissions(mergedRows, savedPayload.pendingEditAccess)
          consumptions.value.forEach((row) => {
            if (row.canEditTransferIssueD) {
              row.transOutDetails = (row.transOutDetails || []).map((det) =>
                normalizeTransOutDetail(det, { editableReplace: true })
              )
            }
          })
          consumptions.value.forEach((_, i) => calculateFields(i, true))
          syncTransferOutDetailsWithCompanyDepartments()
          return
        }

        let partialRows = liveRows
          .sort((a, b) => (a.ingredient || '').localeCompare(b.ingredient || ''))
        partialRows = applySavedTransOutDetailsToRows(partialRows, savedPayload.rows, false)
        consumptions.value = partialRows
        consumptions.value.forEach((_, i) => calculateFields(i))
        syncTransferOutDetailsWithCompanyDepartments()
        return
      }
    } else {
      isFinalized.value = false
      isSavedData.value = false
    }

    const liveRes = await apiFetch(`/consumption/ingredients?departmentId=${selectedDepartment.value}&date=${currentDate.value}`)
    if (liveRes.ok) {
      const data = await liveRes.json()
      consumptions.value = (Array.isArray(data) ? data : [])
        .sort((a, b) => (a.ingredient || '').localeCompare(b.ingredient || ''))
      isSavedData.value = false
      isFinalized.value = false

      consumptions.value.forEach((_, i) => calculateFields(i))
      syncTransferOutDetailsWithCompanyDepartments()
    }
  } catch (err) {
    console.error('Error fetching ingredients:', err)
  } finally {
    loading.value = false
  }
}

async function fetchIncomingOtherTransfers() {
  if (!selectedDepartment.value || !currentDate.value) {
    incomingOtherTransfers.value = []
    return
  }
  try {
    const res = await apiFetch(
      `/consumption/incoming-other-transfers?departmentId=${selectedDepartment.value}&calendar=${currentDate.value}`
    )
    if (!res.ok) {
      incomingOtherTransfers.value = []
      selectedReceiverItemByTransfer.value = {}
      return
    }
    const data = await res.json().catch(() => [])
    incomingOtherTransfers.value = Array.isArray(data) ? data : []
    selectedReceiverItemByTransfer.value = incomingOtherTransfers.value.reduce((acc, row) => {
      acc[row.id] = row.receiverItemId != null ? String(row.receiverItemId) : ''
      return acc
    }, {})
  } catch (err) {
    console.error('Error fetching incoming other transfers:', err)
    incomingOtherTransfers.value = []
    selectedReceiverItemByTransfer.value = {}
  }
}

async function handleIncomingOtherTransferDecision(transferRow, status) {
  if (!transferRow) return

  const transferId = transferRow.id
  if (processingIncomingTransferIds.value[transferId]) return

  if (status === 1) {
    const selectedItemId = Number(selectedReceiverItemByTransfer.value[transferId])
    if (!Number.isFinite(selectedItemId) || selectedItemId <= 0) {
      window.alert('Select Item is required before accepting.')
      return
    }
    if (!window.confirm('Are you sure you want to accept this transfer?')) return
  } else {
    if (!window.confirm('Are you sure you want to reject this transfer?')) return
  }

  processingIncomingTransferIds.value = { ...processingIncomingTransferIds.value, [transferId]: true }
  try {
    const receiverItemId = status === 1
      ? Number(selectedReceiverItemByTransfer.value[transferId])
      : null

    const res = await apiFetch('/consumption/incoming-other-transfers/decision', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        senderDepartmentId: transferRow.senderDepartmentId,
        receiverDepartmentId: transferRow.receiverDepartmentId,
        sentItemId: transferRow.sentItemId,
        type: transferRow.type,
        calendar: transferRow.calendar,
        decision: status,
        receiverItemId,
      }),
    })

    if (!res.ok) {
      const errPayload = await res.json().catch(() => ({}))
      window.alert(errPayload?.message || 'Failed to update transfer status.')
      return
    }

    await fetchIngredients()
    showOtherTransferDropdown.value = incomingOtherTransfers.value.length > 0
  } catch (err) {
    console.error('Error updating incoming other transfer:', err)
    window.alert('Failed to update transfer status.')
  } finally {
    const next = { ...processingIncomingTransferIds.value }
    delete next[transferId]
    processingIncomingTransferIds.value = next
  }
}

async function saveAllRows() {
  if (!selectedDepartment.value || !currentDate.value || consumptions.value.length === 0) return false
  if (isFutureDate.value) return false
  saving.value = true
  saveStatus.value = ''
  try {
    const rows = consumptions.value.map(row => {
      const bbf = Number(row.bbf) || 0
      const storeRecv = Number(row.storeRecv) || 0
      const nonStock = Number(row.nonStock) || 0
      const production = Number(row.production) || 0
      const wastage = Number(row.wastage) || 0
      const transIn1 = Number(row.transIn1) || 0
      const transIn2 = Number(row.transIn2) || 0

      const salesManual = Number(row.salesManual) || 0
      const salesSystem = Number(row.salesSystem) || 0
      const mgmt = Number(row.mgmt) || 0
      const room = Number(row.room) || 0
      const promo = Number(row.promo) || 0
      const coCost = Number(row.coCost) || 0
      const spoilage = Number(row.spoilage) || 0
      const other = Number(row.other) || 0
      const transOut1 = Number(row.transOut1) || 0
      const transOut2 = Number(row.transOut2) || 0
      const spoiledItem = Number(row.spoiledItem) || 0
      const catering = Number(row.catering) || 0
      const ending = Number(row.ending) || 0

      const recalculatedTotalAvail = computeTotalAvail(row, {
        bbf, storeRecv, nonStock, production, wastage, transIn1, transIn2,
      })
      const recalculatedTotalUsed = Number((salesManual + salesSystem + mgmt + room + promo + coCost + spoilage + other + transOut1 + transOut2 + spoiledItem + catering).toFixed(2))
      const recalculatedBalance = Number((recalculatedTotalAvail - recalculatedTotalUsed).toFixed(2))
      const recalculatedVariance = Number((ending - recalculatedBalance).toFixed(2))

      const payloadTotalAvail = recalculatedTotalAvail
      const payloadTotalUsed = isFinalized.value
        ? recalculatedTotalUsed
        : (Number(String(row.totalUsed ?? 0).replace(/,/g, '')) || 0)
      const payloadBalance = isFinalized.value
        ? recalculatedBalance
        : (Number(String(row.balance ?? 0).replace(/,/g, '')) || 0)
      const payloadVariance = isFinalized.value
        ? recalculatedVariance
        : (Number(String(row.variance ?? 0).replace(/,/g, '')) || 0)

      return ({
      type: row.type,
      itemId: row.type === 'item' ? (row.ingredientId || row.itemId) : null,
      productId: row.type === 'product' ? (row.ingredientId || row.productId) : null,
      ingredient: row.ingredient,
      bbf: row.bbf || 0,
      storeRecv: row.storeRecv || 0,
      nonStock: row.nonStock || 0,
      production: row.production || 0,
      transIn1: row.transIn1 || 0,
      transIn2: row.transIn2 || 0,
      transInDetails: (row.transInDetails || []).map(d => ({ name: d.name, quantity: d.quantity || 0 })),
      wastage: row.wastage || 0,
      totalAvail: payloadTotalAvail,
      salesManual: row.salesManual || 0,
      salesSystem: row.salesSystem || 0,
      mgmt: row.mgmt || 0,
      room: row.room || 0,
      promo: row.promo || 0,
      coCost: row.coCost || 0,
      spoilage: row.spoilage || 0,
      other: row.other || 0,
      transOut1: row.transOut1 || 0,
      transOut2: row.transOut2 || 0,
      transOutDetails: (row.transOutDetails || []).map(d => {
        const qty = isFinalized.value && row.canEditTransferIssueD
          ? (parseTransferQty(d.quantity) ?? getTransferTSent(d))
          : getTransferTSent(d)
        return {
          id: d.id,
          name: d.name,
          quantity: qty || 0,
          is_confirmed: d.is_confirmed ?? 0,
        }
      }),
      spoiledItem: row.spoiledItem || 0,
      catering: row.catering || 0,
      totalUsed: payloadTotalUsed,
      balance: payloadBalance,
      ending: row.ending || 0,
      variance: payloadVariance,
    })})

    const res = await apiFetch('/consumption/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        departmentId: selectedDepartment.value,
        calendar: currentDate.value,
        rows
      })
    })

    saveStatus.value = res.ok ? 'success' : 'error'
    if (res.ok) {
      await fetchIngredients()
    }
    return !!res.ok
  } catch (err) {
    console.error('Error saving consumption:', err)
    saveStatus.value = 'error'
    return false
  } finally {
    saving.value = false
    setTimeout(() => { saveStatus.value = '' }, 3000)
  }
}

const calculateFields = (index, force = false) => {
  const row = consumptions.value[index]
  if (!row) return
  if (isFutureDate.value) return
  if (isFinalized.value && !force) return

  const bbf = Number(row.bbf) || 0
  const storeRecv = Number(row.storeRecv) || 0
  const nonStock = Number(row.nonStock) || 0
  const production = Number(row.production) || 0
  const wastage = Number(row.wastage) || 0
  const transIn1 = Number(row.transIn1) || 0
  const transIn2 = Number(row.transIn2) || 0

  // Total On Hand: BBF + receipts + non-stock ± production (subtract if item) - wastage + transfers in
  row.totalAvail = computeTotalAvail(row, {
    bbf, storeRecv, nonStock, production, wastage, transIn1, transIn2,
  }).toFixed(2)
  
  const salesManual = Number(row.salesManual) || 0
  const salesSystem = Number(row.salesSystem) || 0
  const mgmt = Number(row.mgmt) || 0
  const room = Number(row.room) || 0
  const promo = Number(row.promo) || 0
  const coCost = Number(row.coCost) || 0
  const spoilage = Number(row.spoilage) || 0
  const other = Number(row.other) || 0
  const transOut1 = Number(row.transOut1) || 0
  const transOut2 = Number(row.transOut2) || 0
  const spoiledItem = Number(row.spoiledItem) || 0
  const catering = Number(row.catering) || 0

  // Total Used = Sum of all consumption fields
  row.totalUsed = (salesManual + salesSystem + mgmt + room + promo + coCost + spoilage + other + transOut1 + transOut2 + spoiledItem + catering).toFixed(2)
  
  // Balance = Total Avail - Total Used
  row.balance = (Number(row.totalAvail) - Number(row.totalUsed)).toFixed(2)
  
  // Variance = Ending - Balance
  row.variance = (Number(row.ending || 0) - Number(row.balance)).toFixed(2)
}

// Removed redundant onMounted
</script>

<style scoped>
.consumption-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th, td {
  padding: 4px 6px;
  text-align: center;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.consumption-table th:nth-child(1),
.consumption-table td:nth-child(1) {
  text-align: center;
}

.consumption-table th:nth-child(2),
.consumption-table td:nth-child(2) {
  text-align: left;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
  box-shadow: 0 1px 0 #e2e8f0;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background-clip: padding-box;
}

th.sticky-col {
  z-index: 30;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
